import { mm } from "@inovitas/infra3dsdk";
import {
  Add,
  DeleteForeverOutlined,
  FileDownloadOutlined,
  MoreHoriz,
  Remove,
  SettingsOutlined,
  VisibilityOffOutlined,
  Visibility,
} from "@mui/icons-material";

import { TreeItem } from "@mui/x-tree-view";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import React, { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";

import { UseActiveLayer } from "shared/common/hooks/UseActiveLayer";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore, Topics } from "shared/common/stores/sidebarStore";
import LayerDownloadDialog from "./LayerDownloadDialog";
import {
  getLayersAddedByUser,
  saveLayersToLocalStorage,
} from "shared/common/utils/layers/localStorageLayers";
import { LayerSymbolizer } from "../layerContent/addLayerItems/layerSettings/LayerSymbolizer";
import {
  MagnetFilled,
  MagnetOutline,
} from "../../../../../common/utils/icons/icons";

interface Props {
  changeExportFile: (id: string, visible: boolean) => void;
  children: Topics;
  index: number[];
  layerMenuID: string;
  setLayerMenuID: Dispatch<SetStateAction<string>>;
  length: number;
  handleCloseEverythingOnRemoveLayer: () => void;
}

export const LayertreeLayers = ({
  changeExportFile,
  children,
  index,
  layerMenuID,
  setLayerMenuID,
  length,
  handleCloseEverythingOnRemoveLayer,
}: Props) => {
  const { t } = useTranslation();
  const apm = defaultStore((state) => state.apm);
  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const changeEditLayerSettings = defaultStore(
    (state) => state.changeEditLayerSettings
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const editPopover = sidebarStore((state) => state.editPopover);
  const changeSelectedTableRows = sidebarStore(
    (state) => state.changeSelectedTableRows
  );
  const activeLayer = UseActiveLayer(viewer);

  const isActive = !!(
    activeLayer &&
    children.layer &&
    activeLayer.id === children.layer.id
  );
  const defaultColor = darkTheme ? "white" : "darkblue.main";

  const rulesWithFilters =
    children.layer?.style?.rules
      .filter((rule) => rule.filter && rule.filter.trim() !== "")
      .filter(
        (rule, index, self) =>
          self.findIndex((r) => r.filter === rule.filter) === index
      ) ?? [];

  const fallbackRule = children.layer?.style?.rules.find(
    (rule) => !rule.filter || rule.filter.trim() === ""
  );

  const legendRules = fallbackRule
    ? [fallbackRule, ...rulesWithFilters]
    : rulesWithFilters;

  const handleVisibilityToggle = (enableVisibility: boolean) => {
    if (!viewer || !children.layer) return;
    children.layer.setVisible(enableVisibility);
    changeExportFile(children.layer.id, enableVisibility);
  };

  const handleSnappingToggle = (enableSnapping: boolean) => {
    if (!viewer || !children.layer) return;
    children.layer.setSnapable(enableSnapping);
  };

  const handleLayerOptions = (item: Topics) => setLayerMenuID(item.key ?? "");
  const handleClose = () => setLayerMenuID("");

  const deleteLayer = (layer: mm.Layer) => {
    const all = getLayersAddedByUser();
    all.forEach((item) => {
      item.layers = item.layers?.filter((ls) => ls.id !== layer.id);
    });
    saveLayersToLocalStorage(all);
    if (viewer) {
      viewer.getScene().getLayerHandler().removeLayer(layer);
      if (activeLayer === null) handleCloseEverythingOnRemoveLayer();
    }
    setLayerMenuID("");
  };

  const editLayer = (layer: mm.Layer) => {
    setLayerMenuID("");
    changeEditLayerSettings(layer);
  };

  const _handleLayerClick = () => {
    changeSelectedTableRows(undefined);
    viewer
      ?.getScene()
      .getLayerHandler()
      .getActiveLayer()
      ?.featureselector.clearSelection();
    if (children.layer)
      viewer?.getScene().getLayerHandler().setActiveLayer(children.layer);
  };

  const hasFilters = rulesWithFilters.length > 0;

  const treeBranchNew = (
    lastItem: boolean,
    horizontal = true,
    vertical = true
  ) => (
    <Box
      id="treeBranchNew"
      sx={{
        width: 24,
        height: 25,
        display: "flex",
        justifyContent: "center",
        alignItems: "start",
      }}
    >
      <Box
        sx={{
          height: lastItem ? "calc(50% + 0.5px)" : "100%",
          borderLeft: vertical
            ? `1px ${darkTheme ? "white" : "#05395277"} solid`
            : "none",
        }}
      ></Box>
      <Box
        sx={{
          alignSelf: "center",
          width: "50%",
          borderBottom: horizontal
            ? `1px ${darkTheme ? "white" : "#05395277"} solid`
            : "none",
        }}
      ></Box>
    </Box>
  );

  const visibilityButton = (
    <IconButton
      onClick={() =>
        monitor(
          apm,
          "change-visibility-layer | Layertree",
          () => handleVisibilityToggle(!children.visible),
          { layername: children.name, visible: !children.visible }
        )
      }
      disableRipple
      sx={{
        padding: 0,
        color: defaultColor,
        "@media (hover: hover)": { ":hover": { color: "secondary.main" } },
      }}
    >
      {children.visible ? (
        <Visibility fontSize="small" />
      ) : (
        <VisibilityOffOutlined fontSize="small" />
      )}
    </IconButton>
  );

  const snappingButton = (
    <IconButton
      onClick={() =>
        monitor(
          apm,
          "change-visibility-layer | Layertree",
          () => handleSnappingToggle(!children.snappingEnabled),
          { layername: children.name, visible: !children.snappingEnabled }
        )
      }
      disableRipple
      sx={{
        padding: 0,
        paddingRight: "2px",
        color: defaultColor,
        "@media (hover: hover)": { ":hover": { color: "secondary.main" } },
      }}
    >
      {children.snappingEnabled ? <MagnetFilled /> : <MagnetOutline />}
    </IconButton>
  );

  const menuItem = (
    onClick: () => void,
    Icon: React.ElementType,
    label: string
  ) => (
    <MenuItem
      onClick={onClick}
      sx={{
        gap: "10px",
        alignItems: "center",
        backgroundColor: darkTheme ? "darkblue.main" : "white",
        color: darkTheme ? "white" : "darkblue.main",
        "@media (hover: hover)": { ":hover": { color: "secondary.main" } },
      }}
    >
      <Icon fontSize="small" />
      <Typography
        variant="body1"
        sx={{
          fontSize: "13px",
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
    </MenuItem>
  );

  const contextMenu = children.addedFromUser && !editPopover && (
    <>
      <IconButton
        id={children.key}
        disableRipple
        onClick={() =>
          monitor(
            apm,
            "show-layer-options | Layertree",
            () => handleLayerOptions(children),
            { layername: children.name }
          )
        }
        className="layer-options-btn"
        sx={{
          padding: 0,
          paddingRight: 0.5,
          color: defaultColor,
          "@media (hover: hover)": {
            ":hover": { color: "secondary.main" },
            display: layerMenuID === children.key ? "inline-flex" : "none",
          },
        }}
      >
        <MoreHoriz fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={document.getElementById(children.key ?? "")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        keepMounted
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={layerMenuID === children.key}
        onClose={handleClose}
        sx={{ padding: 0, marginTop: "6px" }}
        classes={{
          list: darkTheme ? "infra3d-listPopover" : "infra3d-listPopoverWhite",
        }}
      >
        {menuItem(
          () =>
            monitor(
              apm,
              "open-layer-settings | Layertree",
              () => {
                children.layer && editLayer(children.layer);
              },
              { layername: children.name }
            ),
          SettingsOutlined,
          t("edit_layers.edit_layer")
        )}
        {menuItem(
          () => setDialogOpen(true),
          FileDownloadOutlined,
          t("edit_layers.download_layer")
        )}
        {menuItem(
          () =>
            monitor(
              apm,
              "remove-layer | Layertree",
              () => {
                children.layer && deleteLayer(children.layer);
              },
              { layername: children.name }
            ),
          DeleteForeverOutlined,
          t("edit_layers.remove_layer")
        )}
      </Menu>
    </>
  );

  return (
    <>
      {/* simple layer row — no filters */}
      {!hasFilters && (
        <Box
          id="layer"
          sx={{
            display: "flex",
            "&:hover .layer-options-btn": {
              display: "inline-flex",
            },
          }}
        >
          {index.length > 1 &&
            treeBranchNew(index[index.length - 1] === length - 1)}
          <TreeItem
            nodeId={children.key ?? ""}
            label={children.title}
            sx={{
              flex: 1,
              color: isActive ? "secondary.main" : defaultColor,
              "@media (hover: hover)": {
                "&:hover": { color: "secondary.main" },
              },
            }}
            icon={
              <LayerSymbolizer
                style={children.layer!.style}
                icon={children.layer?.geometrytype}
                properties={fallbackRule?.filter}
              />
            }
            onClick={() =>
              monitor(apm, "select-layer | Layertree", _handleLayerClick, {
                layername: children.name,
              })
            }
          />
          {contextMenu}
          {snappingButton}
          {visibilityButton}
        </Box>
      )}

      {/* categorized layer row — has filters */}
      {hasFilters && (
        <>
          <Box
            id="categorizedLayer"
            sx={{
              display: "flex",
              alignItems: "center",
              "&:hover .layer-options-btn": {
                display: "inline-flex",
              },
            }}
          >
            {index.length > 1 &&
              treeBranchNew(index[index.length - 1] === length - 1)}
            <TreeItem
              nodeId={children.key ?? ""}
              label={children.title}
              sx={{
                flex: 1,
                color: isActive ? "secondary.main" : defaultColor,
                "@media (hover: hover)": {
                  "&:hover": { color: "secondary.main" },
                },
              }}
              icon={
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setCategoryOpen((s) => !s);
                  }}
                  sx={{
                    "@media (hover: hover)": {
                      "&:hover": { color: "secondary.main" },
                    },
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {categoryOpen ? (
                    <Remove fontSize="small" />
                  ) : (
                    <Add fontSize="small" />
                  )}
                </Box>
              }
              ContentProps={{
                onClick: () =>
                  monitor(apm, "select-layer | Layertree", _handleLayerClick, {
                    layername: children.name,
                  }),
              }}
            />
            {contextMenu}
            {snappingButton}
            {visibilityButton}
          </Box>

          {/* legend rows */}
          {hasFilters && (
            <Box
              sx={{
                display: "grid",
                gridTemplateRows: categoryOpen ? "1fr" : "0fr",
                transition: "grid-template-rows 0.3s ease",
              }}
            >
              <Box sx={{ overflow: "hidden" }}>
                {legendRules.map((rule, i) => (
                  <Box sx={{ display: "flex" }} key={i}>
                    {treeBranchNew(
                      false,
                      false,
                      index[index.length - 1] !== length - 1 &&
                        index.length !== 1 // If the layer is the last one in the folder or not in a folder
                    )}
                    <TreeItem
                      nodeId={rule.filter || "empty"}
                      label={rule.name || rule.filter || ""}
                      icon={
                        <LayerSymbolizer
                          style={children.layer!.style}
                          icon={children.layer?.geometrytype}
                          properties={rule.filter}
                        />
                      }
                      sx={{
                        marginLeft: index.length === 1 ? "0px" : "27px",
                        "& .MuiTreeItem-content .MuiTreeItem-label": {
                          overflow: "visible",
                        },
                        "& .MuiTreeItem-content": {
                          cursor: "default",
                          color: defaultColor,
                        },
                        "& .MuiTreeItem-content:hover": {
                          cursor: "default",
                          color: defaultColor,
                        },
                        "& .Mui-selected.MuiTreeItem-content": {
                          color: defaultColor,
                        },
                        "& .Mui-selected.MuiTreeItem-content:hover": {
                          color: defaultColor,
                        },
                        "& .Mui-focused, &.Mui-selected.MuiTreeItem-content.Mui-focused":
                          {
                            color: defaultColor,
                          },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}

      {children.layer && (
        <LayerDownloadDialog
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          setLayerMenuID={setLayerMenuID}
          layer={children.layer}
        />
      )}
    </>
  );
};
